import React from "react";
import { useTranslation } from "react-i18next";
import type { EventFormPayload } from "@/services/api/user/events";
import { toTwentyFourHourClock } from "@/utils/eventFormat";

interface EventFormDateStepProps {
  payload: EventFormPayload;
  onChange: <K extends keyof EventFormPayload>(
    key: K,
    value: EventFormPayload[K],
  ) => void;
}

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] text-gray-800 outline-none transition focus:border-[#ffa217] focus:bg-white";
const labelClass = "mb-1.5 block text-[13px] font-semibold text-gray-500";

const switchClass = (active: boolean) =>
  `relative h-6 w-11 shrink-0 rounded-full transition ${
    active ? "bg-[#ffa217]" : "bg-gray-200"
  }`;

const knobClass = (active: boolean) =>
  `absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
    active ? "left-[22px]" : "left-0.5"
  }`;

const Switch: React.FC<{
  label: string;
  active: boolean;
  onToggle: () => void;
}> = ({ label, active, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    onClick={onToggle}
    className="flex w-full items-center justify-between py-1 text-left"
  >
    <span className="text-[13px] font-semibold text-gray-600">{label}</span>
    <span className={switchClass(active)}>
      <span className={knobClass(active)} />
    </span>
  </button>
);

/**
 * Step 3 of the event wizard — start / end date and time, repeat rules and the
 * application deadline. Mirrors the RN `DateAndTimeScreen`.
 */
const EventFormDateStep: React.FC<EventFormDateStepProps> = ({
  payload,
  onChange,
}) => {
  const { t } = useTranslation();

  const setRepeat = (next: "never" | "daily" | "weekly") => {
    onChange("neverRepeat", next === "never");
    onChange("dailyRepeat", next === "daily");
    onChange("weeklyRepeat", next === "weekly");
    if (next === "never") onChange("endDateOn", "");
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="event-start-date">
            {t("Start Date")}
          </label>
          <input
            id="event-start-date"
            type="date"
            value={payload.startDate}
            onChange={(e) => onChange("startDate", e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="event-end-date">
            {t("End Date")}
          </label>
          <input
            id="event-end-date"
            type="date"
            value={payload.endDate}
            onChange={(e) => onChange("endDate", e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
        <Switch
          label={t("All Day")}
          active={payload.allDay}
          onToggle={() => onChange("allDay", !payload.allDay)}
        />

        {!payload.allDay ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="event-start-time">
                {t("Start Time")}
              </label>
              <input
                id="event-start-time"
                type="time"
                value={toTwentyFourHourClock(payload.startTime)}
                onChange={(e) => onChange("startTime", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="event-end-time">
                {t("End Time")}
              </label>
              <input
                id="event-end-time"
                type="time"
                value={toTwentyFourHourClock(payload.endTime)}
                onChange={(e) => onChange("endTime", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
        <p className="mb-2 text-[13px] font-semibold text-gray-600">
          {t("Repeat Daily or Weekly?")}
        </p>
        <div className="space-y-1">
          <Switch
            label={t("Never Repeat")}
            active={payload.neverRepeat}
            onToggle={() => setRepeat("never")}
          />
          <Switch
            label={t("Daily Repeat")}
            active={payload.dailyRepeat}
            onToggle={() => setRepeat(payload.dailyRepeat ? "never" : "daily")}
          />
          <Switch
            label={t("Weekly Repeat")}
            active={payload.weeklyRepeat}
            onToggle={() => setRepeat(payload.weeklyRepeat ? "never" : "weekly")}
          />
        </div>

        {!payload.neverRepeat ? (
          <div className="mt-4">
            <label className={labelClass} htmlFor="event-repeat-end">
              {t("End Time On")}
            </label>
            <input
              id="event-repeat-end"
              type="date"
              value={payload.endDateOn}
              onChange={(e) => onChange("endDateOn", e.target.value)}
              className={inputClass}
            />
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
        <p className="mb-3 text-[13px] font-semibold text-gray-600">
          {t("Deadline Date & Time*")}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="event-deadline-date">
              {t("Date")}
            </label>
            <input
              id="event-deadline-date"
              type="date"
              value={payload.deadlineDate}
              onChange={(e) => onChange("deadlineDate", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="event-deadline-time">
              {t("Start Time")}
            </label>
            <input
              id="event-deadline-time"
              type="time"
              value={toTwentyFourHourClock(payload.deadlineTime)}
              onChange={(e) => onChange("deadlineTime", e.target.value)}
              disabled={!payload.deadlineDate}
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
            />
          </div>
        </div>
        <div className="mt-4">
          <Switch
            label={t("Allow User to apply the activity through Uninfo")}
            active={payload.allowJoin}
            onToggle={() => onChange("allowJoin", !payload.allowJoin)}
          />
        </div>
      </div>
    </div>
  );
};

export default EventFormDateStep;
